import React, { useEffect } from "react";
import { MusicHallRegisBookingStyled } from "./styled";
import { useForm, Controller, useFieldArray } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import moment from "moment";
import { useTranslation } from "react-i18next";
import { Buttons, Icons, Inputs } from "components";

const MAX_ALTERNATIVE_DATES = 2;

// ลูกค้าขอซ่อนคำอธิบายสีในปฏิทินไว้ก่อน (จะเปิดแสดงในเฟสถัดไป)
// การทำงานของปฏิทิน/สีต่าง ๆ ยังเหมือนเดิม แค่ไม่แสดงข้อความอธิบาย
const SHOW_CALENDAR_LEGEND = false;

const DATE_KEY = (date) => (date ? moment(date).format("YYYY-MM-DD") : "");

const TIME_VALUE = (option) => (option?.value ? option.value : "");

const TIME_OPTION = (value) => (value ? { value: value, label: value } : "");

// ตัวกรอง dropdown เวลา ใช้ร่วมกันทั้งวันหลักและวันสำรอง
const FILTER_END_TIME = (startTime) =>
  !startTime?.value
    ? MUSIC_HALL_TIME_OPTIONS
    : MUSIC_HALL_TIME_OPTIONS.filter(
        (e) => parseFloat(e.value) > parseFloat(startTime.value),
      );

// เวลาเริ่มงานต้องอยู่ในช่วงเตรียมงาน
const FILTER_EVENT_START_TIME = (setupStart, setupEnd) => {
  if (!setupStart?.value) return MUSIC_HALL_TIME_OPTIONS;
  if (!setupEnd?.value) {
    return MUSIC_HALL_TIME_OPTIONS.filter(
      (e) => parseFloat(e.value) >= parseFloat(setupStart.value),
    );
  }
  return MUSIC_HALL_TIME_OPTIONS.filter(
    (e) =>
      parseFloat(e.value) >= parseFloat(setupStart.value) &&
      parseFloat(e.value) < parseFloat(setupEnd.value),
  );
};

// เวลาสิ้นสุดงานต้องหลังเวลาเริ่มงาน และไม่เกินเวลาเก็บงาน
const FILTER_EVENT_END_TIME = (eventStart, setupEnd) => {
  if (!eventStart?.value) return MUSIC_HALL_TIME_OPTIONS;
  if (!setupEnd?.value) {
    return MUSIC_HALL_TIME_OPTIONS.filter(
      (e) => parseFloat(e.value) > parseFloat(eventStart.value),
    );
  }
  return MUSIC_HALL_TIME_OPTIONS.filter(
    (e) =>
      parseFloat(e.value) > parseFloat(eventStart.value) &&
      parseFloat(e.value) <= parseFloat(setupEnd.value),
  );
};

// เวลาของแต่ละแถวต้องเรียงถูกลำดับ: เตรียม -> เริ่มงาน -> จบงาน -> เก็บงาน
const IS_VALID_TIME_RANGE = (row) => {
  const setupStart = parseFloat(TIME_VALUE(row?.setup_cleanup_start_time));
  const setupEnd = parseFloat(TIME_VALUE(row?.setup_cleanup_end_time));
  const eventStart = parseFloat(TIME_VALUE(row?.event_start_time));
  const eventEnd = parseFloat(TIME_VALUE(row?.event_end_time));
  if ([setupStart, setupEnd, eventStart, eventEnd].some((e) => isNaN(e))) {
    return false;
  }
  return (
    setupStart < setupEnd && eventStart < eventEnd && eventStart >= setupStart && eventEnd <= setupEnd
  );
};

// ช่องเวลาของวันสำรอง ใช้กฎเดียวกับวันหลัก แต่อ่านค่าจากแถวของตัวเอง
const ALTERNATIVE_TIME_GROUPS = [
  {
    title: "Set-up & Clean-out Time",
    fields: [
      {
        name: "setup_cleanup_start_time",
        label: "From",
        getOptions: () => MUSIC_HALL_TIME_OPTIONS,
      },
      {
        name: "setup_cleanup_end_time",
        label: "To",
        getOptions: (row) => FILTER_END_TIME(row?.setup_cleanup_start_time),
      },
    ],
  },
  {
    title: "Exact Event Time",
    fields: [
      {
        name: "event_start_time",
        label: "From",
        getOptions: (row) =>
          FILTER_EVENT_START_TIME(
            row?.setup_cleanup_start_time,
            row?.setup_cleanup_end_time,
          ),
      },
      {
        name: "event_end_time",
        label: "To",
        getOptions: (row) =>
          FILTER_EVENT_END_TIME(
            row?.event_start_time,
            row?.setup_cleanup_end_time,
          ),
      },
    ],
  },
];

// JSON จาก query string -> ค่าที่ react-hook-form ใช้ได้ (date เป็น Date, เวลาเป็น option object)
const PARSE_ALTERNATIVE_VALUES = (value) => {
  if (!value) return [];
  let list = value;
  if (typeof value === "string") {
    try {
      list = JSON.parse(value);
    } catch (error) {
      return [];
    }
  }
  if (!Array.isArray(list)) return [];
  return list
    .filter((e) => e && e.booking_date)
    .map((e) => ({
      date: new Date(e.booking_date),
      setup_cleanup_start_time: TIME_OPTION(e.setup_cleanup_start_time),
      setup_cleanup_end_time: TIME_OPTION(e.setup_cleanup_end_time),
      event_start_time: TIME_OPTION(e.event_start_time),
      event_end_time: TIME_OPTION(e.event_end_time),
    }));
};

const MusicHallRegisBooking = ({
  availabilityByDate,
  initialValues,
  isDisabledForm,
  onBack,
  onSubmit,
}) => {
  const schema = yup.object().shape({
    booking_date: yup.string().required("required"),
    // typeError กัน yup โชว์ข้อความดิบ ("must be a `object` type...") ตอนค่ายังว่าง
    setup_cleanup_start_time: yup
      .object()
      .typeError("required")
      .required("required"),
    setup_cleanup_end_time: yup
      .object()
      .typeError("required")
      .required("required"),
    event_start_time: yup.object().typeError("required").required("required"),
    event_end_time: yup.object().typeError("required").required("required"),
    alternative_booking_dates: yup
      .array()
      .max(MAX_ALTERNATIVE_DATES, "Maximum 2 alternative dates allowed")
      .of(
        yup.object().shape({
          // mixed().required() ปล่อย "" ผ่าน จึงต้องเช็คค่าว่างเอง
          date: yup.mixed().test("required", "required", (value) => !!value),
          setup_cleanup_start_time: yup
            .object()
            .typeError("required")
            .required("required"),
          setup_cleanup_end_time: yup
            .object()
            .typeError("required")
            .required("required"),
          event_start_time: yup
            .object()
            .typeError("required")
            .required("required"),
          event_end_time: yup
            .object()
            .typeError("required")
            .required("required"),
        }),
      )
      .test(
        "no_duplicate",
        "Alternative dates must not be duplicated",
        (list) => {
          const dates = (list || [])
            .map((e) => DATE_KEY(e.date))
            .filter((e) => e);
          return new Set(dates).size === dates.length;
        },
      )
      .test(
        "valid_time_range",
        "Please check the event time of the alternative date",
        (list) => (list || []).every((e) => IS_VALID_TIME_RANGE(e)),
      ),
  });

  const {
    control,
    handleSubmit,
    watch,

    setValue,
    formState: { errors },
  } = useForm({
    resolver: yupResolver(schema),
    defaultValues: {
      ...initialValues,
      setup_cleanup_start_time: initialValues?.setup_cleanup_start_time
        ? {
            value: initialValues.setup_cleanup_start_time,
            label: initialValues.setup_cleanup_start_time,
          }
        : "",
      setup_cleanup_end_time: initialValues?.setup_cleanup_end_time
        ? {
            value: initialValues.setup_cleanup_end_time,
            label: initialValues.setup_cleanup_end_time,
          }
        : "",
      event_start_time: initialValues?.event_start_time
        ? {
            value: initialValues.event_start_time,
            label: initialValues.event_start_time,
          }
        : "",
      event_end_time: initialValues?.event_end_time
        ? {
            value: initialValues.event_end_time,
            label: initialValues.event_end_time,
          }
        : "",
      // query string เก็บวันสำรองเป็น JSON เพราะแต่ละวันมีเวลาของตัวเองแล้ว
      alternative_booking_dates: PARSE_ALTERNATIVE_VALUES(
        initialValues?.alternative_booking_dates,
      ),
    },
  });

  const { fields, append, remove } = useFieldArray({
    control,
    name: "alternative_booking_dates",
  });

  const { t } = useTranslation(["common", "form", "room"]);

  const bookingDate = watch("booking_date");
  const alternativeDates = watch("alternative_booking_dates");

  // music hall จองวันและเวลาซ้ำกันได้ จึงปิดเฉพาะวันหยุด/ห้องปิด ไม่สนใจคิวจองของคนอื่น
  const mainDateCalendarProps = {
    tileDisabled: ({ date, view }) =>
      view === "month" && !!availabilityByDate?.[DATE_KEY(date)]?.isHoliday,
    tileClassName: ({ date, view }) =>
      view === "month" && availabilityByDate?.[DATE_KEY(date)]?.isHoliday
        ? "tile_closed"
        : null,
  };

  // ปฏิทินวันสำรอง: ปิดวันหยุด / วันเดียวกับวันหลัก / วันเดียวกับวันสำรองอีกช่อง
  const getAlternativeCalendarProps = (index) => ({
    tileDisabled: ({ date, view }) => {
      if (view !== "month") return false;
      const key = DATE_KEY(date);
      if (availabilityByDate?.[key]?.isHoliday) return true;
      if (bookingDate && DATE_KEY(bookingDate) === key) return true;
      return (alternativeDates || []).some(
        (e, i) => i !== index && e?.date && DATE_KEY(e.date) === key,
      );
    },
    tileClassName: ({ date, view }) => {
      if (view !== "month") return null;
      const key = DATE_KEY(date);
      if (availabilityByDate?.[key]?.isHoliday) return "tile_closed";
      if (bookingDate && DATE_KEY(bookingDate) === key) return "tile_main_date";
      return null;
    },
  });

  const setupStartTime = watch("setup_cleanup_start_time");
  const setupEndTime = watch("setup_cleanup_end_time");
  const eventStartTime = watch("event_start_time");
  const eventEndTime = watch("event_end_time");

  // ใช้ตัวกรองชุดเดียวกับวันสำรอง จะได้ไม่มีกฎเวลา 2 ชุดให้หลุดจากกัน
  const getFilteredEndTimeOptions = (startTime) => FILTER_END_TIME(startTime);

  const getFilteredEventStartTimeOptions = () =>
    FILTER_EVENT_START_TIME(setupStartTime, setupEndTime);

  const getFilteredEventEndTimeOptions = () =>
    FILTER_EVENT_END_TIME(eventStartTime, setupEndTime);

  // Reset end time if it's invalid
  useEffect(() => {
    if (setupStartTime && setupEndTime) {
      if (parseFloat(setupEndTime.value) <= parseFloat(setupStartTime.value)) {
        setValue("setup_cleanup_end_time", "");
      }
    }
  }, [setupStartTime, setupEndTime, setValue]);

  useEffect(() => {
    if (eventStartTime && eventEndTime) {
      if (parseFloat(eventEndTime.value) <= parseFloat(eventStartTime.value)) {
        setValue("event_end_time", "");
      }
    }
  }, [eventStartTime, eventEndTime, setValue]);

  // Reset event times if setup times change and make them invalid
  useEffect(() => {
    if (setupStartTime && eventStartTime) {
      if (parseFloat(eventStartTime.value) < parseFloat(setupStartTime.value)) {
        setValue("event_start_time", "");
        setValue("event_end_time", "");
      }
    }
    if (setupEndTime && eventStartTime) {
      if (parseFloat(eventStartTime.value) >= parseFloat(setupEndTime.value)) {
        setValue("event_start_time", "");
        setValue("event_end_time", "");
      }
    }
    if (setupEndTime && eventEndTime) {
      if (parseFloat(eventEndTime.value) > parseFloat(setupEndTime.value)) {
        setValue("event_end_time", "");
      }
    }
  }, [setupStartTime, setupEndTime, eventStartTime, eventEndTime, setValue]);

  return (
    <MusicHallRegisBookingStyled>
      <form onSubmit={handleSubmit(onSubmit)}>
        <div className="datetime_layout">
          <div className="datetime_text1">
            {t("Event Date", { ns: "room" })}
          </div>
          <div className="datetime_text2">
            ({t("The actual date of your event", { ns: "room" })})
          </div>
          <Controller
            control={control}
            render={({ field }) => (
              <Inputs.DateTime
                {...field}
                disabled={isDisabledForm}
                minDate={new Date()}
                calendarProps={mainDateCalendarProps}
                // placeholder={t('test')}
                errors={errors.booking_date?.message}
              />
            )}
            name="booking_date"
            defaultValue=""
          />
          {SHOW_CALENDAR_LEGEND && (
            <div className="legend_layout">
              <div className="legend_item">
                <span className="legend_dot busy" />
                {t("Partially booked - you can still pick this day", {
                  ns: "room",
                })}
              </div>
              <div className="legend_item">
                <span className="legend_dot closed" />
                {t("Holiday or room closed - cannot be selected", {
                  ns: "room",
                })}
              </div>
            </div>
          )}
        </div>
        <div className="set_clean_layout">
          <div className="set_clean_text1">
            {t("Set-up & Clean-out Time", { ns: "room" })}
          </div>
          <div className="set_clean_text2">
            (
            {t("Include your full time slot, e.g., 9:00 AM – 12:00 PM", {
              ns: "room",
            })}
            )
          </div>
          <div className="time">
            <div className="time_layout">
              <div className="time_text1">{t("From", { ns: "common" })}</div>
              <div className="time_input_wrap">
                <Controller
                  control={control}
                  render={({ field }) => (
                    <Inputs.DropdownSelect
                      {...field}
                      disabled={isDisabledForm}
                      options={MUSIC_HALL_TIME_OPTIONS}
                      placeholder={"00:00"}
                      menuPlacement="auto"
                      errors={errors.setup_cleanup_start_time?.message}
                    />
                  )}
                  name="setup_cleanup_start_time"
                  defaultValue={"13:00"}
                />
              </div>
            </div>
            <div className="time_layout">
              <div className="time_text2">{t("To", { ns: "common" })}</div>
              <div className="time_input_wrap">
                <Controller
                  control={control}
                  render={({ field }) => (
                    <Inputs.DropdownSelect
                      {...field}
                      disabled={isDisabledForm}
                      options={getFilteredEndTimeOptions(setupStartTime)}
                      placeholder={"00:00"}
                      menuPlacement="top"
                      errors={errors.setup_cleanup_end_time?.message}
                    />
                  )}
                  name="setup_cleanup_end_time"
                  defaultValue=""
                />
              </div>
            </div>
          </div>
        </div>
        <div className="set_clean_layout">
          <div className="set_clean_text1">
            {t("Exact Event Time", { ns: "room" })}
          </div>
          <div className="set_clean_text2">
            (
            {t(
              "When the event itself begins and ends, e.g., 9:30 AM – 11:30 AM",
              { ns: "room" },
            )}
            )
          </div>
          <div className="time">
            <div className="time_layout">
              <div className="time_text1">{t("From", { ns: "common" })}</div>
              <div className="time_input_wrap">
                <Controller
                  control={control}
                  render={({ field }) => (
                    <Inputs.DropdownSelect
                      {...field}
                      disabled={isDisabledForm}
                      menuPlacement="top"
                      options={getFilteredEventStartTimeOptions()}
                      placeholder={"00:00"}
                      errors={errors.event_start_time?.message}
                    />
                  )}
                  name="event_start_time"
                  defaultValue=""
                />
              </div>
            </div>
            <div className="time_layout">
              <div className="time_text2">{t("To", { ns: "common" })}</div>
              <div className="time_input_wrap">
                <Controller
                  control={control}
                  render={({ field }) => (
                    <Inputs.DropdownSelect
                      {...field}
                      disabled={isDisabledForm}
                      menuPlacement="top"
                      options={getFilteredEventEndTimeOptions()}
                      placeholder={"00:00"}
                      errors={errors.event_end_time?.message}
                    />
                  )}
                  name="event_end_time"
                  defaultValue=""
                />
              </div>
            </div>
          </div>
        </div>
        <div className="alternative_layout">
          {fields.map((item, index) => (
            <div key={item.id} className="alternative_item">
              <div className="alternative_head">
                <div className="datetime_text1">
                  {t("Event Date (Alternate Date)", { ns: "room" })}{" "}
                  {index + 1}
                </div>
                {!isDisabledForm && (
                  <div
                    className="alternative_remove"
                    onClick={() => remove(index)}
                  >
                    <Icons.Close width={11} height={11} color="#4B1E78" />
                  </div>
                )}
              </div>
              <div className="datetime_text2">
                (
                {t(
                  "Choose other possible dates in case the first date is unavailable.",
                  { ns: "room" },
                )}
                )
              </div>
              <Controller
                control={control}
                render={({ field }) => (
                  <Inputs.DateTime
                    {...field}
                    disabled={isDisabledForm}
                    minDate={new Date()}
                    calendarProps={getAlternativeCalendarProps(index)}
                    errors={
                      errors.alternative_booking_dates?.[index]?.date?.message
                    }
                  />
                )}
                name={`alternative_booking_dates.${index}.date`}
                defaultValue=""
              />
              {ALTERNATIVE_TIME_GROUPS.map((group) => (
                <div key={group.title} className="alternative_time_layout">
                  <div className="set_clean_text1">
                    {t(group.title, { ns: "room" })}
                  </div>
                  <div className="time">
                    {group.fields.map((e) => (
                      <div key={e.name} className="time_layout">
                        <div className="time_text1">
                          {t(e.label, { ns: "common" })}
                        </div>
                        <div className="time_input_wrap">
                          <Controller
                            control={control}
                            render={({ field }) => (
                              <Inputs.DropdownSelect
                                {...field}
                                disabled={isDisabledForm}
                                menuPlacement="auto"
                                options={e.getOptions(alternativeDates?.[index])}
                                placeholder={"00:00"}
                                errors={
                                  errors.alternative_booking_dates?.[index]?.[
                                    e.name
                                  ]?.message
                                }
                              />
                            )}
                            name={`alternative_booking_dates.${index}.${e.name}`}
                            defaultValue=""
                          />
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              ))}
              {SHOW_CALENDAR_LEGEND && (
                <div className="legend_layout">
                  <div className="legend_item">
                    <span className="legend_dot closed" />
                    {t(
                      "Already booked, holiday or room closed - cannot be selected",
                      { ns: "room" },
                    )}
                  </div>
                  <div className="legend_item">
                    <span className="legend_dot main_date" />
                    {t("Same as the main event date - cannot be selected", {
                      ns: "room",
                    })}
                  </div>
                </div>
              )}
            </div>
          ))}
          {errors.alternative_booking_dates?.message && (
            <div className="alternative_errors">
              {t(errors.alternative_booking_dates.message, { ns: "common" })}
            </div>
          )}
          {!isDisabledForm && fields.length < MAX_ALTERNATIVE_DATES && (
            <div
              className="alternative_add"
              onClick={() => append({ date: "" })}
            >
              {`+ ${t("Add an alternative event date", { ns: "room" })}`}
            </div>
          )}
        </div>
        <div className="button_layout">
          <div className="button1">
            <Buttons.BgStandard
              theme_white_purple
              label={t("Back", { ns: "common" })}
              onClick={onBack}
            />
          </div>
          <div className="button2">
            <Buttons.BgStandard
              theme_white_purple={isDisabledForm}
              label={t("Next", { ns: "common" })}
              type="submit"
            />
          </div>
        </div>
      </form>
    </MusicHallRegisBookingStyled>
  );
};

const MUSIC_HALL_TIME_OPTIONS = [
  { value: "07.00", label: "07:00" },
  { value: "07.30", label: "07:30" },
  { value: "08.00", label: "08:00" },
  { value: "08.30", label: "08:30" },
  { value: "09.00", label: "09:00" },
  { value: "09.30", label: "09:30" },
  { value: "10.00", label: "10:00" },
  { value: "10.30", label: "10:30" },
  { value: "11.00", label: "11:00" },
  { value: "11.30", label: "11:30" },
  { value: "12.00", label: "12:00" },
  { value: "12.30", label: "12:30" },
  { value: "13.00", label: "13:00" },
  { value: "13.30", label: "13:30" },
  { value: "14.00", label: "14:00" },
  { value: "14.30", label: "14:30" },
  { value: "15.00", label: "15:00" },
  { value: "15.30", label: "15:30" },
  { value: "16.00", label: "16:00" },
  { value: "16.30", label: "16:30" },
  { value: "17.00", label: "17:00" },
  { value: "17.30", label: "17:30" },
  { value: "18.00", label: "18:00" },
  { value: "18.30", label: "18:30" },
  { value: "19.00", label: "19:00" },
  { value: "19.30", label: "19:30" },
  { value: "20.00", label: "20:00" },
  { value: "20.30", label: "20:30" },
  { value: "21.00", label: "21:00" },
  { value: "21.30", label: "21:30" },
  { value: "22.00", label: "22:00" },
];

MusicHallRegisBooking.propTypes = {};

export default MusicHallRegisBooking;
